<script lang="ts">
    import {ignoreUnusedProperties, isTouchDevice} from 'shared/utils/custom-utils';
    import {fly} from 'svelte/transition';
    import {onMount, tick} from 'svelte';
    import UIcon from 'shared/ui-widgets/uicons/UIcon.svelte';

    export let responsive = false;
    export let additionalContainerClasses = '';

    let touchDevice = isTouchDevice();

    let tableContainerElement: HTMLDivElement;
    let tableContainerScrollLeft = 0;
    let tableContainerScrollWidth = 0;
    let tableContainerWidth = 0;
    let horizontalOverflowing = false;
    let overflowLeft = false;
    let overflowRight = false;

    let windowHeight = 0;
    let windowWidth = 0;
    let windowScrollY = 0;

    let overflowButtonsTopOffset = 0;
    $: if (responsive) calculateOverflowButtonsTopOffset(tableContainerElement, windowHeight, windowWidth, windowScrollY);
    $: if (responsive) calculateTableOverflowValues(tableContainerElement, windowWidth);

    onMount(() => {
        calculateTableOverflowValues(tableContainerElement);

        tick().then(() => {
            calculateOverflowButtonsTopOffset(tableContainerElement, windowHeight, windowWidth, windowScrollY);
        });
    });

    function calculateOverflowButtonsTopOffset(container: HTMLDivElement, viewportHeight: number, viewportWidth: number, windowScroll: number) {
        ignoreUnusedProperties(container, windowScroll, viewportWidth); // We only use those to trigger reactivity

        if (!tableContainerElement || !responsive) {
            return;
        }

        touchDevice = isTouchDevice();

        const tableRect = tableContainerElement.getBoundingClientRect();
        const isTopOverflowing = tableRect.top < 0;
        const isBottomOverflowing = tableRect.bottom > viewportHeight;

        if (tableRect.top > viewportHeight) {
            return;
        }

        if (isTopOverflowing && isBottomOverflowing) {
            const topOffset = Math.abs(tableRect.top);
            overflowButtonsTopOffset = topOffset + viewportHeight / 2;
        }

        if (!isTopOverflowing && isBottomOverflowing) {
            const availableViewportHeight = viewportHeight - tableRect.top;
            overflowButtonsTopOffset = availableViewportHeight / 2;
        }

        if (isTopOverflowing && !isBottomOverflowing) {
            const topOffset = Math.abs(tableRect.top);
            const availableViewportHeight = tableRect.bottom;
            overflowButtonsTopOffset = topOffset + availableViewportHeight / 2;
        }

        if (!isTopOverflowing && !isBottomOverflowing) {
            overflowButtonsTopOffset = tableRect.height / 2;
        }
    }

    function calculateTableOverflowValues(container?: HTMLDivElement, viewportWidth?: number) {
        ignoreUnusedProperties(viewportWidth, container); // We only use those to trigger reactivity

        if (!tableContainerElement || !responsive) {
            return;
        }

        tableContainerScrollLeft = tableContainerElement.scrollLeft;
        tableContainerScrollWidth = tableContainerElement.scrollWidth;
        tableContainerWidth = tableContainerElement.clientWidth;

        horizontalOverflowing = tableContainerScrollWidth > tableContainerWidth;
        overflowLeft = tableContainerScrollLeft > 0;
        overflowRight = tableContainerScrollLeft + tableContainerWidth < tableContainerScrollWidth - 1;
    }

    const handleScrollLeftClick = () => {
        tableContainerElement.scrollTo({
            left: Math.max(0, tableContainerScrollLeft - tableContainerWidth),
            behavior: 'smooth'
        });
    };

    const handleScrollRightClick = () => {
        tableContainerElement.scrollTo({
            left: Math.min(Math.max(0, tableContainerScrollLeft + tableContainerWidth - 250), tableContainerScrollWidth - tableContainerWidth),
            behavior: 'smooth'
        });
    };
</script>

<svelte:window bind:scrollY={windowScrollY}
               bind:innerHeight={windowHeight}
               bind:innerWidth={windowWidth}/>

<div class="responsive-table-container"
     class:with-overflow-buttons={horizontalOverflowing && responsive}>

    <div class="table-container {additionalContainerClasses}"
         class:is-responsive={responsive}
         bind:this={tableContainerElement}
         on:scroll={() => calculateTableOverflowValues()}>

        <slot/>

        {#if responsive && horizontalOverflowing && !touchDevice}
            {#if overflowLeft}
                <button type="button"
                        aria-label="Left scroll"
                        style="top: {overflowButtonsTopOffset}px"
                        class="horizontal-overflow-btn scroll-left"
                        transition:fly={{x: -15}}
                        on:click={handleScrollLeftClick}>

                    <UIcon icon="arrow-small-left"/>
                </button>
            {/if}

            {#if overflowRight}
                <button type="button"
                        aria-label="Right scroll"
                        style="top: {overflowButtonsTopOffset}px"
                        class="horizontal-overflow-btn scroll-right"
                        transition:fly={{x: 15}}
                        on:click={handleScrollRightClick}>

                    <UIcon icon="arrow-small-right"/>
                </button>
            {/if}
        {/if}
    </div>
</div>

<style lang="less">
    @import (reference) "styles/portaro.media-queries.less";
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro.themes.less";

    @overflowBtnSize: 38px;
    @overflowIconSize: 18px;

    .responsive-table-container {
        position: relative;

        &.with-overflow-buttons {
            overflow: hidden;
        }

        .horizontal-overflow-btn {
            position: absolute;
            width: @overflowBtnSize;
            height: @overflowBtnSize;
            border-radius: 50%;
            outline: 1px solid transparent;
            outline-offset: 1px;
            background-color: var(--table-header-accent-color);
            border: 1px solid rgba(0, 0, 0, 0.15);
            font-size: @overflowIconSize;
            display: flex;
            color: white;
            align-items: center;
            justify-content: center;
            transform: translateY(-50%);
            box-shadow: rgba(0, 0, 0, 0.2) 0 3px 8px;
            transition: opacity 0.3s ease-in-out, border-color 0.3s ease-in-out, outline-color 0.3s ease-in-out, visibility 0.3s ease-in-out, transform 0.3s ease-in-out;

            &:hover,
            &:active {
                opacity: 0.85;
                outline-color: var(--accent-blue);
            }

            &:active {
                opacity: 0.65;
            }

            &.scroll-left {
                left: 0;
                transform: translate(@spacing-ml, -50%);
            }

            &.scroll-right {
                right: 0;
                transform: translate(calc(@spacing-ml * -1), -50%);
            }
        }

        .table-container.is-responsive {
            overflow-y: hidden;

            @media screen and (max-width: @screen-xs-max) {
                margin-bottom: 0;
            }
        }
    }

    :global {
        .table-container {
            .kp-button,
            .kp-button-style-link {
                white-space: nowrap;
            }
        }
    }
</style>